import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { useThemeColors } from "../context/ThemeContext";
import { HEADING_FONT } from "../theme/fonts";
import { useLocations, useSublocations } from "../hooks/queries";
import { COUNTRIES, INDIAN_STATES } from "../constants/geo";

function SectionHeader({ icon, title, subtitle, colors }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          background:
            "linear-gradient(135deg, rgba(79, 70, 229, 0.18) 0%, rgba(124, 58, 237, 0.18) 100%)",
          color: "#818cf8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: 14,
            fontWeight: 700,
            color: colors.text,
            lineHeight: 1.2,
            mt: "2px",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function EditClient() {
  const navigate = useNavigate();
  const { id } = useParams();
  const colors = useThemeColors();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [gstin, setGstin] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [country, setCountry] = useState("India");
  const [stateName, setStateName] = useState("");
  const [locationId, setLocationId] = useState("");
  const [sublocationId, setSublocationId] = useState("");
  const [regDate, setRegDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);

  const { data: locations = [] } = useLocations();
  const { data: sublocations = [] } = useSublocations(locationId);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Load the client's current values once, into the form fields.
  useEffect(() => {
    let active = true;
    api
      .get(`/clients/${id}`)
      .then((c) => {
        if (!active) return;
        setName(c.name || "");
        setCompanyName(c.companyName || "");
        setClientCode(c.clientCode || "");
        setAccountNumber(c.accountNumber || "");
        setGstin(c.gstin || "");
        setEmail(c.email || "");
        setPhone(c.phone || "");
        setBillingAddress(c.billingAddress || "");
        setCountry(c.country || "India");
        setStateName(c.state || "");
        setLocationId(c.locationId || "");
        setSublocationId(c.sublocationId || "");
        setRegDate(c.regDate ? String(c.regDate).slice(0, 10) : "");
        setNotes(c.notes || "");
      })
      .catch((err) => {
        if (active) setLoadError(err.message || "Failed to load client");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  async function handleSubmit(e) {
    if (e?.preventDefault) e.preventDefault();
    setSubmitError("");
    if (!name.trim()) {
      setSubmitError("Client name is required");
      return;
    }
    setSaving(true);

    const payload = {
      name: name.trim(),
      companyName,
      clientCode,
      accountNumber,
      gstin,
      email,
      phone,
      billingAddress,
      country,
      state: stateName,
      locationId: locationId || null,
      sublocationId: sublocationId || null,
      regDate,
      notes,
    };

    try {
      await api.put(`/clients/${id}`, payload);
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setSnackbarOpen(true);
      setTimeout(() => navigate("/clients"), 700);
    } catch (err) {
      setSubmitError(err.message || "Failed to update client");
      setSaving(false);
    }
  }

  const labelSx = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    mb: 1,
  };

  // Glass card — identical recipe to the Add Client / Edit Domain pages.
  const cardSx = {
    background: colors.glassBg,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: 3,
    boxShadow: colors.shadowSm,
    p: 3,
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#818cf8" },
    },
    "& input": { color: colors.text },
    "& textarea": { color: colors.text },
    "& input::placeholder, & textarea::placeholder": {
      color: colors.textFaint,
      opacity: 1,
    },
  };

  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 3,
  };

  return (
    <DashboardLayout>
      {/* Back link */}
      <Button
        onClick={() => navigate("/clients")}
        startIcon={<ArrowBackIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: colors.textMuted,
          mb: 2,
          ml: -1,
          "&:hover": { background: colors.hover, color: colors.text },
        }}
      >
        Back to Clients
      </Button>

      {/* Hero card */}
      <Box
        sx={{
          position: "relative",
          background:
            "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          overflow: "hidden",
          color: "#fff",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            filter: "blur(40px)",
          }}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ position: "relative", zIndex: 1, minWidth: 0 }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              background: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ContactsOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                opacity: 0.85,
              }}
            >
              Edit Client
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: { xs: 24, md: 32 },
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.1,
                wordBreak: "break-word",
              }}
            >
              {name || "Client"}
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
              Update this client's details
            </Typography>
          </Box>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#818cf8" }} />
        </Box>
      ) : loadError ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: colors.text }}>{loadError}</Typography>
        </Box>
      ) : (
        /* Form cards */
        <Stack spacing={3}>
          {/* Section 1: Client Details */}
          <Box sx={cardSx}>
            <SectionHeader
              icon={<BadgeOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Client Details"
              subtitle="Name, code and tax identity"
              colors={colors}
            />
            <Divider sx={{ my: 2, borderColor: colors.divider }} />
            <Stack spacing={3}>
              <Box sx={gridSx}>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Client Name *
                  </Typography>
                  <TextField
                    placeholder="e.g. Rohit Prajapati"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    fullWidth
                    sx={fieldSx}
                  />
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Company Name
                  </Typography>
                  <TextField
                    placeholder="e.g. Obitel Prime Realtors"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    fullWidth
                    sx={fieldSx}
                  />
                </Box>
              </Box>
              <Box sx={gridSx}>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Client Code
                  </Typography>
                  <TextField
                    placeholder="e.g. 202786"
                    value={clientCode}
                    onChange={(e) => setClientCode(e.target.value)}
                    fullWidth
                    sx={fieldSx}
                  />
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Account Number
                  </Typography>
                  <TextField
                    placeholder="e.g. ACC-1001"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    fullWidth
                    sx={fieldSx}
                  />
                </Box>
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>
                  GSTIN
                </Typography>
                <TextField
                  placeholder="e.g. 27ABCDE1234F1Z5"
                  value={gstin}
                  onChange={(e) => setGstin(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
            </Stack>
          </Box>

          {/* Section 2: Contact */}
          <Box sx={cardSx}>
            <SectionHeader
              icon={<AlternateEmailOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Contact Information"
              subtitle="How to reach this client"
              colors={colors}
            />
            <Divider sx={{ my: 2, borderColor: colors.divider }} />
            <Box sx={gridSx}>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Email
                </Typography>
                <TextField
                  placeholder="client@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Phone
                </Typography>
                <TextField
                  placeholder="9820000000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </Box>

          {/* Section: Location & Registration */}
          <Box sx={cardSx}>
            <SectionHeader
              icon={<PlaceOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Location & Registration"
              subtitle="Geography and registration date"
              colors={colors}
            />
            <Divider sx={{ my: 2, borderColor: colors.divider }} />
            <Stack spacing={3}>
              <Box sx={gridSx}>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Country
                  </Typography>
                  <TextField
                    select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    fullWidth
                    sx={fieldSx}
                  >
                    {COUNTRIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    State
                  </Typography>
                  <TextField
                    select
                    value={stateName}
                    onChange={(e) => setStateName(e.target.value)}
                    fullWidth
                    sx={fieldSx}
                  >
                    <MenuItem value="">— Select State —</MenuItem>
                    {INDIAN_STATES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <Box sx={gridSx}>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Location
                  </Typography>
                  <TextField
                    select
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      setSublocationId("");
                    }}
                    fullWidth
                    sx={fieldSx}
                  >
                    <MenuItem value="">— Select Location —</MenuItem>
                    {locations.map((l) => (
                      <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>
                    Sublocation
                  </Typography>
                  <TextField
                    select
                    value={sublocationId}
                    onChange={(e) => setSublocationId(e.target.value)}
                    fullWidth
                    disabled={!locationId}
                    sx={fieldSx}
                  >
                    <MenuItem value="">— Select Sublocation —</MenuItem>
                    {sublocations.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <Box sx={{ maxWidth: { sm: "calc(50% - 12px)" } }}>
                <Typography component="label" sx={labelSx}>
                  Registration Date
                </Typography>
                <TextField
                  type="date"
                  value={regDate}
                  onChange={(e) => setRegDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Box>
            </Stack>
          </Box>

          {/* Section: Address & Notes */}
          <Box sx={cardSx}>
            <SectionHeader
              icon={<NotesOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Address & Notes"
              subtitle="Billing address and anything else"
              colors={colors}
            />
            <Divider sx={{ my: 2, borderColor: colors.divider }} />
            <Stack spacing={3}>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Billing Address
                </Typography>
                <TextField
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Notes
                </Typography>
                <TextField
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={fieldSx}
                />
              </Box>
            </Stack>
          </Box>

          {/* Actions */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            sx={{
              pt: 2,
              borderTop: `1px solid ${colors.divider}`,
            }}
          >
            <Button
              type="button"
              onClick={() => navigate("/clients")}
              sx={{
                textTransform: "none",
                color: colors.textMuted,
                px: 3,
                py: 1.25,
                borderRadius: 2,
                "&:hover": { background: colors.hover, color: colors.text },
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={saving || !name.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 3.5,
                py: 1.25,
                borderRadius: 2,
                color: "#fff",
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                "&:hover": {
                  boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  color: "rgba(255,255,255,0.6)",
                  background: colors.cardBorder,
                },
                transition: "all 0.15s ease",
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </Stack>
        </Stack>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled">
          Client Updated Successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={5000}
        onClose={() => setSubmitError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" variant="filled">
          {submitError}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default EditClient;
